<script lang="ts">
  import { onMount } from 'svelte';
  import { Consultorios, type Profesional } from '../lib/api';
  import { readImageAsDataUrl } from '../lib/branding.svelte';

  let lista = $state<Profesional[]>([]);
  let cargando = $state(false);
  let error = $state<string | null>(null);
  let aviso = $state<string | null>(null);

  async function recargar() {
    cargando = true;
    error = null;
    try {
      lista = await Consultorios.profesionales(false);
    } catch (e: any) {
      error = e?.message ?? 'Error al cargar médicos.';
    } finally {
      cargando = false;
    }
  }

  async function guardar(p: Profesional) {
    if (!p.id) return;
    error = null;
    aviso = null;
    try {
      await Consultorios.actualizarProf(p.id, p);
      aviso = `Firma/sello guardados para ${p.nombre}.`;
    } catch (e: any) {
      error = e?.message ?? 'No se pudo guardar.';
    }
  }

  async function onImage(ev: Event, p: Profesional, campo: 'firmaUrl' | 'selloUrl') {
    const file = (ev.target as HTMLInputElement).files?.[0];
    if (!file) return;
    error = null;
    try {
      p[campo] = await readImageAsDataUrl(file);
    } catch (e: any) {
      error = e?.message ?? 'Error al cargar imagen.';
    }
    (ev.target as HTMLInputElement).value = '';
  }

  onMount(() => { void recargar(); });
</script>

<section class="panel">
  <h3>Firmas y sellos de médicos</h3>
  <p class="hint">Aparecen en recetas imprimibles de farmacia. Sube la imagen (PNG/JPG) o pega la <strong>URL directa</strong> de la imagen (que termine en .png/.jpg/.jpeg, ej. i.ibb.co/…). Evita pegar la página del visor (ibb.co/…) porque no carga como imagen.</p>

  {#if error}<p class="err">{error}</p>{/if}
  {#if aviso}<p class="ok">{aviso}</p>{/if}

  {#if cargando}
    <p class="muted">Cargando…</p>
  {:else if lista.length === 0}
    <p class="muted">No hay profesionales registrados. Créalos en Consultorios / clínicas asociadas.</p>
  {:else}
    <table>
      <thead>
        <tr><th>Médico</th><th>Colegiado</th><th>Firma</th><th>Sello</th><th></th></tr>
      </thead>
      <tbody>
        {#each lista as p (p.id)}
          <tr>
            <td>{p.nombre}</td>
            <td>{p.colegiado ?? '—'}</td>
            <td>
              <input bind:value={p.firmaUrl} placeholder="URL firma" />
              <input type="file" accept="image/*" onchange={(e) => onImage(e, p, 'firmaUrl')} />
              {#if p.firmaUrl}<img src={p.firmaUrl} alt="" class="thumb" />{/if}
            </td>
            <td>
              <input bind:value={p.selloUrl} placeholder="URL sello" />
              <input type="file" accept="image/*" onchange={(e) => onImage(e, p, 'selloUrl')} />
              {#if p.selloUrl}<img src={p.selloUrl} alt="" class="thumb" />{/if}
            </td>
            <td><button type="button" onclick={() => guardar(p)}>Guardar</button></td>
          </tr>
        {/each}
      </tbody>
    </table>
  {/if}
</section>

<style>
  .panel { background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 1rem; margin-top: 1rem; }
  h3 { margin: 0 0 0.35rem; color: var(--brand-deep); }
  .hint, .muted { color: var(--text-secondary); font-size: 0.85rem; }
  .err { color: var(--danger); font-size: 0.85rem; }
  .ok { color: var(--success); font-size: 0.85rem; }
  table { width: 100%; border-collapse: collapse; margin-top: 0.75rem; font-size: 0.82rem; }
  th, td { border: 1px solid var(--border); padding: 0.45rem 0.5rem; vertical-align: top; }
  th { background: var(--brand-soft); color: var(--brand-deep); text-align: left; }
  input { width: 100%; box-sizing: border-box; margin-bottom: 0.25rem; font: inherit; padding: 0.35rem; border: 1px solid var(--border); border-radius: 6px; }
  .thumb { display: block; max-height: 40px; max-width: 100px; margin-top: 0.25rem; object-fit: contain; }
  button { font: inherit; padding: 0.3rem 0.6rem; border-radius: 6px; border: 1px solid var(--border); cursor: pointer; background: var(--bg-elevated); }
</style>
